import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';

import { AppDispatch, RootState } from '../../../store/store';
import { fetchSearchResults, setSearchFilters } from '../store/coursesSlice';
import { CourseSearchResult, SearchFilters } from '../api/courses';
import EmptyState from '../../../commodity/components/ui/EmptyState';
import ErrorState from '../../../commodity/components/ui/ErrorState';
import { formatMoney } from '../../../commodity/format/money';
import { useIsNarrow } from '../../../commodity/hooks/useViewport';
import CatalogFilters from '../components/CatalogFilters';
// SUBJECTS is shared with course authoring — if the two lists drifted, a newly
// created course would never appear under its own subject filter.
import { COURSE_LANGUAGE_OPTIONS, GRADE_LEVEL_LABELS } from '../domain/catalog';
import { courseZone, formatDayAndTime } from '../time/zonedTime';
import {
  courseDetailPath,
  filtersFromParams,
  filtersToParams,
  narrowingFilters,
} from '../domain/catalog-context';

const languageLabel = (code?: string): string | undefined =>
  COURSE_LANGUAGE_OPTIONS.find((o) => o.value === code)?.label;

/** A grade as a parent reads it, not as the catalog stores it. */
const gradeLabel = (code: string): string =>
  GRADE_LEVEL_LABELS[code as keyof typeof GRADE_LEVEL_LABELS] ?? code;

/**
 * One course, built only from facts the catalog was given (PRT-02-FR-07).
 *
 * What is not on it matters as much as what is. The card used to carry the
 * teacher's trust score in the slot where a rating belongs, which is the one
 * substitution D-P03-02 and PRT-BR-P03-09 name outright: the course rating and
 * the teacher's standing are different claims. The rating here is the course's
 * own (DEC-S2-S03-05); the teacher's lives on the teacher's profile.
 */
const CourseRatingLine: React.FC<{ rating: CourseSearchResult['rating'] }> = ({ rating }) => {
  if (!rating) return <span>Valoración no disponible</span>;
  if (rating.count === 0) return <span>Sin reseñas todavía</span>;
  return (
    <span>
      ★ {rating.average.toFixed(1)} ({rating.count})
    </span>
  );
};

/**
 * DEC-S2-S03-05, DEC-S2-S04-05 — when this course can next be taken, from its
 * offer's materialized meetings. A full course, one with no dates and one whose
 * dates could not be read each say so; none of them falls back to a timetable.
 */
const NextAvailability: React.FC<{ course: CourseSearchResult }> = ({ course }) => {
  if (course.nextAvailableAt && course.timezone) {
    const tz = courseZone(course.timezone);
    return (
      <div style={{ fontSize: 12, color: '#1c4f80' }}>
        🗓 Próxima clase: {formatDayAndTime(course.nextAvailableAt, tz)} ({tz.zone})
        {course.durationMode === 'FIXED' && course.durationWeeks
          ? ` · ${course.durationWeeks} semanas`
          : ''}
      </div>
    );
  }
  if (course.scheduleType !== 'RECURRING') {
    return <div style={{ fontSize: 12, color: '#666' }}>Horario por cita</div>;
  }
  return (
    <div style={{ fontSize: 12, color: '#666' }}>
      {course.seatsLeft === 0 ? 'Sin cupo por ahora' : 'Fechas no disponibles por ahora'}
    </div>
  );
};

const CourseCardBody: React.FC<{ course: CourseSearchResult; to: string }> = ({ course, to }) => {
  const language = languageLabel(course.language);

  return (
    <article
      style={{
        border: '1px solid #d1d5da',
        borderRadius: 8,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <span
          style={{
            fontSize: 12,
            backgroundColor: '#e7f5ff',
            color: '#1971c2',
            padding: '2px 8px',
            borderRadius: 4,
          }}
        >
          {course.subject}
        </span>
        {language && <span style={{ fontSize: 12, color: '#666' }}>{language}</span>}
      </div>

      <h3 style={{ margin: 0, fontSize: 16 }}>{course.title}</h3>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12 }}>
        <CourseRatingLine rating={course.rating} />
        {course.teacherVerified && <span style={{ color: '#2b8a3e' }}>Profesor verificado</span>}
      </div>

      {/* A fixed timetable is the single most decision-relevant fact for a
          parent scanning the catalog, so it sits above the tags. */}
      <NextAvailability course={course} />

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {course.gradeLevel.map((g) => (
          <span
            key={g}
            style={{
              fontSize: 11,
              backgroundColor: '#f1f3f5',
              padding: '2px 6px',
              borderRadius: 4,
            }}
          >
            {gradeLabel(g)}
          </span>
        ))}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginTop: 'auto',
        }}
      >
        <span style={{ fontSize: 14, color: '#868e96' }}>{course.duration} min</span>
        {/* PRT-BR-P03-11: the amount the catalog holds is per session, and the
            label says so. It read as a bare number beside a hand-typed `$`,
            which is a price with no unit and no stated currency. */}
        <span style={{ fontSize: 14 }}>
          <strong style={{ fontSize: 18, color: '#1971c2' }}>
            {formatMoney(course.price, course.currency)}
          </strong>{' '}
          <span style={{ color: '#868e96' }}>por sesión</span>
        </span>
      </div>

      {/* PRT-02-FR-12 — the card's one action, carrying the result set it was
          chosen from so the detail page can hand it back. */}
      <Link
        to={to}
        style={{
          marginTop: 4,
          display: 'block',
          textAlign: 'center',
          padding: '8px 12px',
          borderRadius: 6,
          border: '1px solid #1971c2',
          color: '#1971c2',
          textDecoration: 'none',
        }}
      >
        Ver detalles
      </Link>
    </article>
  );
};

const CourseSearch: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const narrow = useIsNarrow();
  const { searchResults, loading, error, nextToken } = useSelector(
    (state: RootState) => state.courses,
  );

  /**
   * The address bar holds the filters, and nothing else does.
   *
   * It used to be component state, so the card linked to a bare
   * `/courses/:id`, the detail page's back button went to `/courses`, and a
   * parent who narrowed the catalog to three courses lost all of it by opening
   * one of them.
   */
  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);
  const applyFilters = (next: SearchFilters) =>
    setSearchParams(filtersToParams(next), { replace: true });

  /**
   * Edits the phone has taken but not committed (PRT-BR-P03-16).
   *
   * `null` means the panel is closed, which is also the only state in which
   * the sidebar's controls commit on change. One value rather than an open
   * flag beside a draft, because the two can disagree — an open panel with no
   * draft silently filters live, which is the rule the spec warns against.
   */
  const [staged, setStaged] = useState<SearchFilters | null>(null);

  useEffect(() => {
    dispatch(setSearchFilters(filters));
    dispatch(fetchSearchResults(filters));
  }, [dispatch, filters]);

  // Closing the panel discards whatever was staged in it. The rule the spec
  // warns about is a panel that commits on close: the person who backed out
  // then has filters they explicitly did not apply.
  useEffect(() => {
    if (!narrow) setStaged(null);
  }, [narrow]);

  const active = narrowingFilters(filters);
  const activeFilterCount = active.length;

  const filterControls = (
    <CatalogFilters
      value={staged ?? filters}
      onChange={(next) => (staged === null ? applyFilters(next) : setStaged(next))}
    />
  );

  const resetLink = (
    <Link
      to="/courses"
      style={{
        display: 'block',
        textAlign: 'center',
        padding: 8,
        borderRadius: 4,
        border: '1px solid #d1d5da',
        color: '#1c1c1c',
        textDecoration: 'none',
      }}
    >
      Quitar filtros ({activeFilterCount})
    </Link>
  );

  return (
    <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
      {!narrow && (
        <aside style={{ width: 240, flexShrink: 0 }}>
          <h3>Filtros</h3>
          {filterControls}
          {/* A reset on an unfiltered catalog is a control that does nothing,
              and the one time it matters nobody believes it. */}
          {activeFilterCount > 0 && resetLink}
        </aside>
      )}

      <main style={{ flex: 1, minWidth: 0 }}>
        <h2>Cursos Disponibles</h2>

        {narrow && (
          <div style={{ marginBottom: 16 }}>
            <button
              type="button"
              aria-expanded={staged !== null}
              onClick={() => setStaged(staged === null ? filters : null)}
              style={{
                width: '100%',
                padding: 10,
                borderRadius: 6,
                border: '1px solid #d1d5da',
                background: '#fff',
                cursor: 'pointer',
              }}
            >
              {/* The count is on the closed control on purpose: a collapsed
                  panel must never be the only place a live filter is visible. */}
              {activeFilterCount > 0 ? `Filtros (${activeFilterCount})` : 'Filtros'}
            </button>

            {staged !== null && (
              <div
                role="group"
                aria-label="Filtros del catálogo"
                style={{
                  marginTop: 8,
                  padding: 16,
                  border: '1px solid #d1d5da',
                  borderRadius: 8,
                }}
              >
                {filterControls}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => {
                      applyFilters(staged);
                      setStaged(null);
                    }}
                    style={{
                      flex: 1,
                      padding: 10,
                      borderRadius: 6,
                      border: 'none',
                      background: '#1971c2',
                      color: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    Aplicar
                  </button>
                  <button
                    type="button"
                    onClick={() => setStaged(null)}
                    style={{
                      flex: 1,
                      padding: 10,
                      borderRadius: 6,
                      border: '1px solid #d1d5da',
                      background: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    Cancelar
                  </button>
                </div>
                {activeFilterCount > 0 && <div style={{ marginTop: 8 }}>{resetLink}</div>}
              </div>
            )}
          </div>
        )}

        {loading && <p>Cargando cursos...</p>}

        {/* PRT-BR-P03-18 / A6 — a catalog that failed to load is not a catalog
            with nothing in it. Both used to render at once: the screen said
            "something went wrong" and "no courses found" in the same breath,
            and the second one is a claim about supply that nobody had made. */}
        {!loading && error && (
          <ErrorState
            action="cargar el catálogo"
            error={error}
            onRetry={() => dispatch(fetchSearchResults(filters))}
          />
        )}

        {/* PRT-02-FR-14 / A1 — an empty result, said out loud, with the thing
            that changes it. Which thing depends on why it is empty, and the two
            reasons are not the same sentence. */}
        {!loading && !error && searchResults.length === 0 && activeFilterCount > 0 && (
          <EmptyState
            message="Ningún curso coincide con estos filtros."
            detail="No ampliamos la búsqueda por tu cuenta: lo que ves es exactamente lo que pediste."
            action={{ label: 'Quitar los filtros', to: '/courses' }}
          />
        )}

        {!loading && !error && searchResults.length === 0 && activeFilterCount === 0 && (
          <EmptyState
            message="Todavía no hay cursos publicados."
            detail="Cuando un profesor publique el primero, aparecerá aquí."
          />
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 16,
          }}
        >
          {searchResults.map((course) => (
            <CourseCardBody
              key={course.listingId}
              course={course}
              to={courseDetailPath(course.listingId, filters)}
            />
          ))}
        </div>

        {nextToken && !loading && (
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <button
              onClick={() => dispatch(fetchSearchResults({ ...filters, nextToken }))}
              style={{
                padding: '10px 24px',
                backgroundColor: '#0366d6',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              Cargar mas
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default CourseSearch;
